<script>
import { store } from '../../functions/mixins';
import * as sdk from '../../functions/sdk';
import { buildAppUrl, getPremadeImageSizeLimit, testUrlBuilder } from '../../functions/helpers';
import UiHeadline from '../ui/Headline';
import UiSubheader from '../ui/Subheader';
import UiButton from '../ui/Button';
import UiLoading from '../ui/Loading';
import UiScrollbar from '../ui/Scrollbar';
import UiTabs from '../ui/Tabs';
import UiInput from '../ui/Input';
import UiCheckbox from '../ui/Checkbox';
import deepEqual from 'deep-equal';
import ColourPicker from '../ui/InlineColorPicker';
import { VueCropper }  from "vue-cropper"

export default {
    mixins: [store],
    props: {
        value: Object,
    },
    components: {
        UiHeadline,
        UiSubheader,
        UiLoading,
        UiScrollbar,
        UiTabs,
        UiInput,
        UiCheckbox,
        ColourPicker,
        UiButton,
        VueCropper,
    },
    data: () => ({
        data: null,
        showUrlBuilder: true,
        urlBuilderAction: '',
        urlBuilderParams: [],
    }),
    computed: {
        transitionsSorted() {
            return this.transitions === null ? [] : this.transitions.sort((a, b) => b.id - a.id);
        },
        isUrlBuilderOpen() {
            return this.data.message.type === 'html' && this.showUrlBuilder;
        },
        isHtmlOpen() {
            return this.data.message.type === 'html' && !this.isUrlBuilderOpen;
        },
        isPremadeOpen() {
            return this.data.message.type === 'premade';
        },
        isFeedOpen() {
            return this.data.message.type === 'feed';
        },
        isImageTooBig() {
            const img = this.data.message.premade.image;
            return img && img.size > getPremadeImageSizeLimit();
        },
        isPremadeFirst() {
            return !!this.userInfo.premadeFirst;
        },
        premadeTabs() {
            const tabs = [
                {
                    title: this.$t('stepMessage.premadeTabBasic'),
                    value: 'basic',
                },
                {
                    title: this.$t('stepMessage.premadeTabLink'),
                    value: 'link',
                },
                {
                    title: this.$t('stepMessage.premadeTabYesNo'),
                    value: 'yesno',
                },
            ];
            //this.userInfo.extraTemplates = 'furge,furge02,coupon,seyu1,seyu2';
            if (this.userInfo.extraTemplates) {
                const templates = this.userInfo.extraTemplates.split(',').map(i => i.trim());
                if (templates.includes('furge')) {
                    tabs.push({
                        title: this.$t('stepMessage.premadeTabFurge01'),
                        value: 'furge01',
                    });
                }
                if (templates.includes('furge02')) {
                    tabs.push({
                        title: this.$t('stepMessage.premadeTabFurge02'),
                        value: 'furge02',
                    });
                }
                if (templates.includes('coupon')) {
                    tabs.push({
                        title: this.$t('stepMessage.premadeTabCoupon'),
                        value: 'coupon',
                    });
                }
                if (templates.includes('seyu1')) {
                    tabs.push({
                        title: this.$t('stepMessage.premadeTabSeyu1'),
                        value: 'seyu1',
                    });
                }
                if (templates.includes('seyu2')) {
                    tabs.push({
                        title: this.$t('stepMessage.premadeTabSeyu2'),
                        value: 'seyu2',
                    });
                }
            }
            return tabs;
        },
        furge01Colors() {
            return [
                '#981B1E',
                '#EE2A25',
                '#F2E624',
                '#FFCB04',
                '#74BF44',
                '#CBDB2A',
                '#2194D2',
                '#6FCEF5',
            ];
        },
        isUrlBuilderActionValid() {
            return testUrlBuilder([this.urlBuilderAction])[0];
        },
        isUrlBuilderParamsValid() {
            return testUrlBuilder(this.urlBuilderParams.map(i => i[0]));
        },
        cropperStyle() {
            if (!this.data.message.feed.image) {
                return 'display: none';
            }
            const { width, height, containerWidth } = this.data.message.feed.imageInfo;
            const ratio = 450 / height;
            let w = width * ratio;
            w = Math.min(containerWidth, w);
            w = Math.max(200, w);
            return `width: ${w}px`;
        },
    },
    created() {
        this.data = Object.assign({}, this.value);
        this.data.message.premade.image = this.value.message.premade.image;
        if (this.data.message.html.url.length === 0) {
            this.urlBuilderAction = 'main';
        }
    },
    methods: {
        setType(type, showUrlBuilder = false) {
            this.data.message.type = type;
            this.showUrlBuilder = showUrlBuilder;
            if (showUrlBuilder) {
                this.data.message.html.url = '';
                if (!this.isUrlBuilderActionValid) return;
                for (let i = 0; i < this.isUrlBuilderParamsValid.length; i++) {
                    if (!this.isUrlBuilderParamsValid[i]) return;
                }
                this.data.message.html.url = buildAppUrl(this.urlBuilderAction, this.urlBuilderParams);
            }
        },
        onPremadeImagePicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                if (files && files[0]) {
                    this.data.message.premade.image = files[0];
                }
            }
        },
        onFeedImagePicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                const file = files[0];
                const image = new Image();
                image.onload = () => {
                    this.data.message.feed.image = file;
                    this.data.message.feed.imageUrl = URL.createObjectURL(file);
                    this.data.message.feed.imageInfo = {
                        width: image.width,
                        height: image.height,
                        containerWidth: this.$refs.feedCropperWrapper.clientWidth,
                    };
                };
                image.src = URL.createObjectURL(file);
            }
        },
        onFeedImageCropped(e) {
            e.target.cropper.getCroppedCanvas({
                width: Math.floor(1024 * ((this.userInfo?.feedImageScale || 100) / 100)),
                height: 1024,
                fillColor: '#FFF',
            }).toBlob((blob) => {
                this.data.message.feed.imageCropped = new File([blob], 'cropped.png', { type: 'image/png' });
            }, 'image/png', 1);
        },
        onFurge01ImagePicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                const file = files[0];
                const image = new Image();
                image.onload = () => {
                    this.data.message.premade.furge01.image = file;
                    this.data.message.premade.furge01.realImage = image;
                };
                image.src = URL.createObjectURL(file);
            }
        },
        onFurge02ImagePicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                const file = files[0];
                const image = new Image();
                image.onload = () => {
                    this.data.message.premade.furge02.image = file;
                    this.data.message.premade.furge02.realImage = image;
                };
                image.src = URL.createObjectURL(file);
            }
        },
        onCouponImagePicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                this.data.message.premade.coupon.image = files[0];
            }
        },
        onSeyu1BackroundPicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                const file = files[0];
                const image = new Image();
                image.onload = () => {
                    this.data.message.premade.seyu1.background = file;
                    this.data.message.premade.seyu1.realBackground = image;
                };
                image.src = URL.createObjectURL(file);
            }
        },
        onSeyu1LogoPicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                const file = files[0];
                const image = new Image();
                image.onload = () => {
                    this.data.message.premade.seyu1.logo = file;
                    this.data.message.premade.seyu1.realLogo = image;
                };
                image.src = URL.createObjectURL(file);
            }
        },
        onSeyu2BackroundPicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                const file = files[0];
                const image = new Image();
                image.onload = () => {
                    this.data.message.premade.seyu2.background = file;
                    this.data.message.premade.seyu2.realBackground = image;
                };
                image.src = URL.createObjectURL(file);
            }
        },
        onSeyu2LogoPicked(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (files && files[0]) {
                const file = files[0];
                const image = new Image();
                image.onload = () => {
                    this.data.message.premade.seyu2.logo = file;
                    this.data.message.premade.seyu2.realLogo = image;
                };
                image.src = URL.createObjectURL(file);
            }
        },
    },
    watch: {
        value: {
            deep: true,
            handler(nextVal) {
                if (!deepEqual(nextVal, this.data)) {
                    this.data = Object.assign({}, nextVal);
                }
            },
        },
        data: {
            deep: true,
            handler(nextVal) {
                if (!deepEqual(nextVal, this.value)) {
                    const payload = Object.assign({}, nextVal);
                    this.$emit('input', payload);
                }
            },
        },
        urlBuilderAction(nextVal) {
            if (!testUrlBuilder([nextVal])[0]) {
                this.data.message.html.url = '';
                return;
            }
            for (let i = 0; i < this.isUrlBuilderParamsValid.length; i++) {
                if (!this.isUrlBuilderParamsValid[i]) {
                    this.data.message.html.url = '';
                    return;
                }
            }
            this.data.message.html.url = buildAppUrl(nextVal, this.urlBuilderParams);
        },
        urlBuilderParams: {
            deep: true,
            handler(nextVal) {
                if (!this.isUrlBuilderActionValid) {
                    this.data.message.html.url = '';
                    return;
                }
                const test = testUrlBuilder(nextVal.map(i => i[0]));
                for (let i = 0; i < test.length; i++) {
                    if (!test[i]) {
                        this.data.message.html.url = '';
                        return;
                    }
                }
                this.data.message.html.url = buildAppUrl(this.urlBuilderAction, nextVal);
            },
        }
    },
}
</script>

<template>
    <div class="StepMessage">
        <!--
        <ui-subheader>{{ $t('stepMessage.step') }} 4<span>/5</span></ui-subheader>
        <ui-headline>{{ $t('stepMessage.title') }}</ui-headline>
        -->
        <div class="Accordion">
            <div :class="['Section', {Open: isUrlBuilderOpen}]">
                <div class="Header" @click="setType('html', true)">
                    <div class="Title">{{ $t('stepMessage.urlBuilderTitle') }}</div>
                    <div class="Subtitle">{{ $t('stepMessage.urlBuilderSubtitle') }}</div>
                </div>
                <div class="Content">
                    <ui-input
                        v-model="urlBuilderAction"
                        :label="$t('stepMessage.urlBuilderLabelAction')"
                        :debounce="400"
                        :error="!isUrlBuilderActionValid && urlBuilderAction.length > 0"
                        placeholder="main"
                    />
                    <div class="BuilderUrlParamContainer" v-for="(i, key) in urlBuilderParams" :key="key">
                        <ui-input
                            v-model="i[0]"
                            :placeholder="$t('stepMessage.urlBuilderPlaceholderParamName')"
                            :debounce="400"
                            :error="!isUrlBuilderParamsValid[key] && i[0].length > 0"
                        />
                        <ui-input
                            v-model="i[1]"
                            :placeholder="$t('stepMessage.urlBuilderPlaceholderParamValue')"
                            :debounce="400"
                        />
                        <ui-button
                            @click.native="urlBuilderParams = urlBuilderParams.filter((_, idx) => idx !== key)"
                            type="icon"
                        >X</ui-button>
                    </div>
                    <div class="BuilderUrlFooter">
                        <ui-button type="primary" @click.native="urlBuilderParams.push(['', ''])">{{ $t('stepMessage.urlBuilderAddParam') }}</ui-button>
                    </div>
                </div>
            </div>
            <div :class="['Section', {Open: isHtmlOpen, Order5: isPremadeFirst}]">
                <div class="Header" @click="setType('html')">
                    <div class="Title">{{ $t('stepMessage.linkTitle') }}</div>
                    <div class="Subtitle">{{ $t('stepMessage.linkSubtitle') }}</div>
                </div>
                <div class="Content">
                    <!--
                    <ui-tabs
                        v-model="data.message.html.tab"
                        :tabs="[
                            {title: $t('stepMessage.linkTabUrl'), value: 'web'},
                            {title: $t('stepMessage.linkTabApp'), value: 'app'},
                        ]"
                        class="Indent35"
                    />
                    -->
                    <span></span>
                    <ui-input
                        v-model="data.message.html.url"
                        :label="$t('stepMessage.linkLabelUrl')"
                        :debounce="400"
                        placeholder="https://example.com"
                    />
                </div>
            </div>
            <div :class="['Section', {Open: isPremadeOpen}]" v-if="userInfo.enablePremade === true">
                <div class="Header" @click="setType('premade')">
                    <div class="Title">{{ $t('stepMessage.premadeTitle') }}</div>
                    <div class="Subtitle">{{ $t('stepMessage.premadeSubtitle') }}</div>
                </div>
                <div class="Content">
                    <ui-tabs
                        v-model="data.message.premade.tab"
                        :tabs="premadeTabs"
                        class="Indent35"
                    />
                    <template v-if="['basic', 'link', 'yesno'].includes(data.message.premade.tab)">
                        <ui-input
                            :value="data.message.premade.image ? data.message.premade.image.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeLabelImage')"
                            :hint="$t('stepMessage.premadeHintImage')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.uploader.click()">
                                    <ui-loading class="Small" v-if="data.message.premade.imageUploading" />
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-else="" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="uploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onPremadeImagePicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <div v-if="isImageTooBig" class="Alert">
                            <div class="AlertInner" v-html="$t('stepMessage.premadeImageSizeWarning')" />
                        </div>
                        <ui-input
                            v-model="data.message.premade.url"
                            :label="$t('stepMessage.premadeLabelUrl')"
                            :debounce="400"
                            class="KeepLayout"
                            v-if="data.message.premade.tab === 'link'"
                        />
                    </template>
                    <template v-else-if="data.message.premade.tab === 'furge01'">
                        <ui-input
                            :value="data.message.premade.furge01.image ? data.message.premade.furge01.image.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeFurge01LabelImage')"
                            :hint="$t('stepMessage.premadeFurge01HintImage')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.furge01Uploader.click()" :disabled="data.message.premade.furge01.imageUploading">
                                    <ui-loading class="Small" v-if="data.message.premade.furge01.imageUploading" />
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-else="" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="furge01Uploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onFurge01ImagePicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.furge01.header"
                            :label="$t('stepMessage.premadeFurge01LabelHeader')"
                            :debounce="400"
                            class="KeepLayout"
                        />
                        <ui-input
                            v-model="data.message.premade.furge01.title"
                            :label="$t('stepMessage.premadeFurge01LabelTitle')"
                            :debounce="400"
                            class="KeepLayout"
                        />
                        <ui-input
                            v-model="data.message.premade.furge01.subtitle"
                            :label="$t('stepMessage.premadeFurge01LabelSubtitle')"
                            :debounce="400"
                            class="KeepLayout"
                        />
                        <div class="Colors">
                            <span
                                v-for="c in furge01Colors"
                                :style="{background: c}"
                                :key="c"
                                :class="{Active: data.message.premade.furge01.bg === c}"
                                @click="data.message.premade.furge01.bg = c"
                            />
                        </div>
                        <!--
                        <div class="CheckboxWrapper">
                            <div class="Empty"></div>
                            <div class="Holder">
                                <ui-checkbox
                                    v-model="data.message.premade.furge01.vote"
                                    :label="$t('stepMessage.premadeFurge01LabelYesNo')"
                                />
                            </div>
                        </div>
                        -->
                    </template>
                    <template v-else-if="data.message.premade.tab === 'furge02'">
                        <ui-input
                            :value="data.message.premade.furge02.image ? data.message.premade.furge02.image.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeFurge02LabelImage')"
                            :hint="$t('stepMessage.premadeFurge02HintImage')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.furge02Uploader.click()" :disabled="data.message.premade.furge02.imageUploading">
                                    <ui-loading class="Small" v-if="data.message.premade.furge02.imageUploading" />
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-else="" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="furge02Uploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onFurge02ImagePicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.furge02.message"
                            :label="$t('stepMessage.premadeFurge02LabelMessage')"
                            :debounce="400"
                            class="KeepLayout"
                            type="textarea"
                        />

                    </template>
                    <template v-else-if="data.message.premade.tab === 'coupon'">
                        <ui-input
                            :value="data.message.premade.coupon.image ? data.message.premade.coupon.image.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeCouponLabelImage')"
                            :hint="$t('stepMessage.premadeCouponHintImage')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.couponUploader.click()" :disabled="data.message.premade.coupon.imageUploading">
                                    <ui-loading class="Small" v-if="data.message.premade.coupon.imageUploading" />
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-else="" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="couponUploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onCouponImagePicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.coupon.expire"
                            :label="$t('stepMessage.premadeCouponLabelExpireDate')"
                            :debounce="400"
                            class="KeepLayout"
                            type="datepicker"
                        />
                        <ui-input
                            v-model="data.message.premade.coupon.txtButton"
                            :label="$t('stepMessage.premadeCouponLabelButton')"
                            :debounce="400"
                            class="KeepLayout"
                        />
                        <ui-input
                            v-model="data.message.premade.coupon.txtExpired"
                            :label="$t('stepMessage.premadeCouponLabelExpired')"
                            :debounce="400"
                            @focus="data.message.premade.coupon.overlayExpired = true"
                            @blur="data.message.premade.coupon.overlayExpired = false"
                            class="KeepLayout"
                        />
                        <ui-input
                            v-model="data.message.premade.coupon.txtRedeemed"
                            :label="$t('stepMessage.premadeCouponLabelRedeemed')"
                            :debounce="400"
                            @focus="data.message.premade.coupon.overlayRedeemed = true"
                            @blur="data.message.premade.coupon.overlayRedeemed = false"
                            class="KeepLayout"
                        />
                    </template>
                    <template v-else-if="data.message.premade.tab === 'seyu1'">
                        <ui-input
                            :value="data.message.premade.seyu1.background ? data.message.premade.seyu1.background.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeSeyu1LabelBackground')"
                            :hint="$t('stepMessage.premadeSeyu1HintBackground')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.seyu1BackgroundUploader.click()">
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="seyu1BackgroundUploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onSeyu1BackroundPicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <ui-input
                            :value="data.message.premade.seyu1.logo ? data.message.premade.seyu1.logo.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeSeyu1LabelLogo')"
                            :hint="$t('stepMessage.premadeSeyu1HintLogo')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.seyu1LogoUploader.click()">
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="seyu1LogoUploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onSeyu1LogoPicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.seyu1.message"
                            :label="$t('stepMessage.premadeSeyu1LabelMessage')"
                            :debounce="400"
                            class="KeepLayout WithColorPicker"
                        >
                            <template slot="details">
                                <div class="ColorPicker">
                                    <colour-picker
                                        :color="data.message.premade.seyu1.colors.message"
                                        @input="val => data.message.premade.seyu1.colors.message = val"
                                    />
                                </div>
                            </template>
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.seyu1.title"
                            :label="$t('stepMessage.premadeSeyu1LabelTitle')"
                            :debounce="400"
                            class="KeepLayout WithColorPicker"
                        >
                            <template slot="details">
                                <div class="ColorPicker">
                                    <colour-picker
                                        :color="data.message.premade.seyu1.colors.title"
                                        @input="val => data.message.premade.seyu1.colors.title = val"
                                    />
                                </div>
                            </template>
                        </ui-input>
                    </template>





                    <template v-else-if="data.message.premade.tab === 'seyu2'">
                        <ui-input
                            :value="data.message.premade.seyu2.background ? data.message.premade.seyu2.background.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeSeyu2LabelBackground')"
                            :hint="$t('stepMessage.premadeSeyu2HontBackground')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.seyu2BackgroundUploader.click()">
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="seyu2BackgroundUploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onSeyu2BackroundPicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <ui-input
                            :value="data.message.premade.seyu2.logo ? data.message.premade.seyu2.logo.name : ''"
                            readonly
                            :label="$t('stepMessage.premadeSeyu2LabelLogo')"
                            :hint="$t('stepMessage.premadeSeyu2HintLogo')"
                            :debounce="400"
                            class="KeepLayout"
                        >
                            <template slot="append">
                                <button class="UploadButton" @click="$refs.seyu2LogoUploader.click()">
                                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="13" height="12" viewBox="0 0 13 12" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.717 7.718a.863.863 0 01-1.287 0L7.813 3.807V12H5.188V3.779L1.545 7.718a.863.863 0 01-1.287 0 1.042 1.042 0 010-1.391L5.844.288a.877.877 0 01.644-.289.87.87 0 01.643.289l5.586 6.039a1.04 1.04 0 010 1.391z"/></svg>
                                    <input
                                        type="file"
                                        ref="seyu2LogoUploader"
                                        name="file"
                                        accept="*"
                                        style="display: none"
                                        @change="onSeyu2LogoPicked"
                                    />
                                </button>
                            </template>
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.seyu2.question"
                            :label="$t('stepMessage.premadeSeyu2LabelQuestion')"
                            :debounce="400"
                            class="KeepLayout WithColorPicker"
                        >
                            <template slot="details">
                                <div class="ColorPicker">
                                    <colour-picker
                                        :color="data.message.premade.seyu2.colors.question"
                                        @input="val => data.message.premade.seyu2.colors.question = val"
                                    />
                                </div>
                            </template>
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.seyu2.answerA"
                            :label="$t('stepMessage.premadeSeyu2LabelAnswerA')"
                            :debounce="400"
                            class="KeepLayout WithColorPicker--"
                        >
                            <!--template slot="details">
                                <div class="ColorPicker">
                                    <colour-picker
                                        :color="data.message.premade.seyu2.colors.answerA"
                                        @input="val => data.message.premade.seyu2.colors.answerA = val"
                                    />
                                </div>
                            </template-->
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.seyu2.answerB"
                            :label="$t('stepMessage.premadeSeyu2LabelAnswerB')"
                            :debounce="400"
                            class="KeepLayout WithColorPicker--"
                        >
                            <!--template slot="details">
                                <div class="ColorPicker">
                                    <colour-picker
                                        :color="data.message.premade.seyu2.colors.answerB"
                                        @input="val => data.message.premade.seyu2.colors.answerB = val"
                                    />
                                </div>
                            </template-->
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.seyu2.answerC"
                            :label="$t('stepMessage.premadeSeyu2LabelAnswerC')"
                            :debounce="400"
                            class="KeepLayout WithColorPicker--"
                        >
                            <!--template slot="details">
                                <div class="ColorPicker">
                                    <colour-picker
                                        :color="data.message.premade.seyu2.colors.answerC"
                                        @input="val => data.message.premade.seyu2.colors.answerC = val"
                                    />
                                </div>
                            </template-->
                        </ui-input>
                        <ui-input
                            v-model="data.message.premade.seyu2.answerD"
                            :label="$t('stepMessage.premadeSeyu2LabelAnswerD')"
                            :debounce="400"
                            class="KeepLayout WithColorPicker--"
                        >
                            <!--template slot="details">
                                <div class="ColorPicker">
                                    <colour-picker
                                        :color="data.message.premade.seyu2.colors.answerD"
                                        @input="val => data.message.premade.seyu2.colors.answerD = val"
                                    />
                                </div>
                            </template-->
                        </ui-input>
                    </template>
                </div>
            </div>
            <div :class="['Section', {Open: isFeedOpen}]" v-if="userInfo.enableFeedMessage === true">
                <div class="Header" @click="setType('feed')">
                    <div class="Title">{{ $t('stepMessage.feedTitle') }}</div>
                    <div class="Subtitle">{{ $t('stepMessage.feedSubtitle') }}</div>
                </div>
                <div class="Content">
                    <ui-input
                        v-model="data.message.feed.message"
                        :label="$t('stepMessage.feedLabelMessage')"
                        :placeholder="$t('stepMessage.feedPlaceholderMessage')"
                        :debounce="400"
                        type="textarea"
                        :maxLen="userInfo?.feedMessageLen || 200"
                        class="KeepLayout"
                    />
                    <ui-input
                        v-model="data.message.feed.url"
                        :label="$t('stepMessage.feedLabelUrl')"
                        :debounce="400"
                        placeholder="https://example.com/"
                        class="KeepLayout"
                    />
                    <div class="CropperWrapper" ref="feedCropperWrapper">
                        <div :style="cropperStyle" v-if="data.message.feed.imageUrl">
                            <SlimCropper
                                ref="cropperFeed"
                                :src="data.message.feed.imageUrl"
                                :cropperOptions="{
                                    viewMode: 1,
                                    cropend: onFeedImageCropped,
                                    ready: onFeedImageCropped,
                                    dragMode: 'move',
                                    movable: false,
                                    scalable: false,
                                    zoomable: false,
                                    rotatable: false,
                                    aspectRatio: this.userInfo?.feedImageScale ? (this.userInfo?.feedImageScale / 100) : 1,
                                    cropBoxMovable: true,
                                    cropBoxResizable: true,
                                }"
                                style="margin-bottom: 20px"
                            />
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
                            <UiButton @click.native="$refs.uploaderFeed.click()">{{ $t('stepMessage.feedPickImage') }}</UiButton>
                            <span>{{ $t('stepMessage.feedPickImageInfo') }} {{ Math.floor(1024 * ((this.userInfo?.feedImageScale || 100) / 100)) }} x 1024 px</span>
                        </div>
                        <input
                            type="file"
                            ref="uploaderFeed"
                            name="file"
                            accept=".png,.jpg,jpeg"
                            style="display: none"
                            @change="onFeedImagePicked"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="stylus">
@import '../../stylus/vars.styl'

.WithColorPicker {
    .Details {
        position: relative;
        .ColorPicker {
            position: absolute;
            right: 0;
        }
    }
}

.Accordion
    border 1px solid $cBorder
    display flex
    flex-direction column
    .Section
        position relative
        border-bottom 1px solid $cBorder
        &.Order1
            order 1
        &.Order2
            order 2
        &.Order3
            order 3
        &.Order4
            order 4
        &.Order5
            order 5
        .Header
            padding 25px 35px
            color $cPrimary
            .Title
                font-weight 600
                font-size 28px
            .Subtitle
                font-weight 400
                font-size 12px
        &.Section:not(.Open) .Header {
            cursor: pointer;
        }
        .Content
            max-height 0
            overflow hidden
            transition .3s all
            .Tabs
                position relative
                z-index 1
                .TabItem
                    padding 20px !important
            .Input
                margin-top -1px
                padding 10px 30px 10px 55px !important
                box-shadow none !important
                border none !important
                .Control
                    position relative
                &:nth-child(2)
                    padding-top 30px !important
                &:last-child
                    padding-bottom 30px !important
                &:not(.KeepLayout)
                    .Details
                        width auto !important
                        flex 0 0 auto !important
                    .Control
                        width auto !important
                        flex 1 1 auto !important
            .UploadButton
                position absolute
                top 0
                bottom 0
                right 0
                width 64px
                border 3px solid $cBorder
                background lighten($cBorder, 60%)
                display flex
                justify-content center
                align-items center
                transition .3s all
                cursor pointer
                &:hover
                    border-color $cAccent !important
                    background lighten($cAccent, 80%)
            .StateFocus .UploadButton
                border-color $cAccent !important
            .CheckboxWrapper
                background white
                padding 20px 30px 20px 55px
                .Checkbox .Label
                    font-size 15px
                    font-weight 700
            .Colors
                display flex
                background white
                padding 20px 30px 20px 55px
                justify-content flex-end
                span
                    width 48px
                    height 32px
                    margin 0 0 0 10px
                    border 2px solid #fff
                    box-shadow 1px 2px 3px rgba(0,0,0,.3)
                    cursor pointer
                    transition .3s all
                    &.Active
                        border-color $cAccent
            .BuilderUrlParamContainer
                display: flex
                padding: 15px 30px
                .Input
                    flex: 1 1 auto;
                    margin: 0 5px 0 0;
                    padding: 0 !important;
                    .Details
                        display: none;
                    .Control
                        padding: 0;
            .BuilderUrlFooter
                padding: 15px 30px
                text-align center
            .CropperWrapper
                display flex
                flex-direction column
                align-items center
                padding: 15px 30px
        &.Open
            box-shadow 0 25px 70px 0px rgba(0,0,0,.1)
            .Content
                max-height 3000px
        .Alert
            padding 15px 0
            background white
            .AlertInner
                background-color lighten($cError, 80%)
                border 1px solid $cError
                color $cError
                padding 30px

</style>
